import { TSupportedLanguageKey } from "i18n";
import { isNil, isString } from "lodash-es";
import { booleanType, ensure, ensureArray, numberType, stringType, TType } from "types";

import {
  configDatabaseRecordType,
  configPageableMetaType,
  IDatabaseRecord,
  IPageable,
  IPartialPageable,
} from "@/core/api";
import { codelistType, ICodelist } from "@/data/codelists";
import { IRequestToJoinCompany, requestToJoinCompanyType } from "@/data/companies";
import { ILocation, locationType } from "@/data/locations";
import { IPhone, phoneType } from "@/data/phones";

export interface IUserSettings {
  digitalCommunicationEnabled: boolean;
  zone: string;
  dateFormat: ICodelist;
  numberFormat: ICodelist;
  timeFormat: ICodelist;
}

export const configUserSettingsType: TType<IUserSettings> = {
  digitalCommunicationEnabled: booleanType(false),
  zone: stringType(""),
  dateFormat: v => (isNil(v) ? null : codelistType(v)),
  timeFormat: v => (isNil(v) ? null : codelistType(v)),
  numberFormat: v => (isNil(v) ? null : codelistType(v)),
};

export const userSettingsType = ensure(configUserSettingsType);

export interface IUser extends IDatabaseRecord {
  email: string;
  emailConfirmed: boolean;
  firstName: string;
  language: TSupportedLanguageKey;
  lastName: string;
  lastUsedConsultantProfileId: number | null;
  location: ILocation | null;
  onboardingDisplayed: boolean;
  phone: IPhone | null;
  requestToJoin: IRequestToJoinCompany | null;
  settings: IUserSettings;
  loginOnBehalfRequester: string | null;
  onboardingJobRequestId?: number | null;
}

export interface IUserPartial extends Pick<IUser, "id" | "firstName" | "lastName" | "email"> {}

export const configUserType: TType<IUser> = {
  ...configDatabaseRecordType,
  email: stringType(""),
  emailConfirmed: booleanType(false),
  firstName: stringType(""),
  language: v => (isString(v) ? v.toLowerCase() : "en"),
  lastName: stringType(""),
  lastUsedConsultantProfileId: numberType(null),
  location: v => (isNil(v) ? null : locationType(v)),
  onboardingDisplayed: booleanType(false),
  phone: v => (isNil(v) ? null : phoneType(v)),
  requestToJoin: v => (isNil(v) ? null : requestToJoinCompanyType(v)),
  settings: v => (isNil(v) ? null : userSettingsType(v)),
  loginOnBehalfRequester: stringType(null),
  onboardingJobRequestId: numberType(null),
};

export const userType = ensure(configUserType);

export interface IPageableUser extends IPageable<IUser[]> {}

export const pageableUserTypeConfig: TType<IPageableUser> = {
  ...configPageableMetaType,
  content: ensureArray(userType),
};

export const pageableUserType = ensure(pageableUserTypeConfig);

export interface IPartialPageableUser extends IPartialPageable<IUser[]> {}
